import { Flex, Form, Typography } from "antd";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { COMPANY_ROLE_ENUM, NOTIFICATION_TYPE_ENUM } from "interfaces";
import { observer } from "mobx-react-lite";
import { useNotificationContext } from "providers";
import { useEffect, useState } from "react";

import { NormalizedError } from "services/api/errorHandler";
import { updateCompanyFees, updateRequestFeeSettings } from "services/settingsService";
import { useStores } from "stores";
import { ActionModalConfig } from "stores/ActionModalsStore";
import { components } from "types/openapi";
import { CustomButton, NumberInput } from "ui";
import { formatWithDecimals, parseErrorMessage, requestWithNotify, toFractionOrZero, toNumberOrZero } from "utils";
import { ADDITIONAL_FEES } from "utils/envConstants";
import {
  _greaterOrEqual,
  _isNumericWithPrecision,
  _isRequired,
  _lessOrEqual,
  _lessOrEqualToComputed,
  _lessThan,
  _maxDecimals,
} from "utils/form/simple-validation";

type BaseFeesFieldType = components["schemas"]["SetSettingsRequest"];
type RequestFeeSettingsPayload = components["schemas"]["UpdateRequestFeeSettingsRequest"];

type UpdateFeeModalProps = {
  config?: ActionModalConfig;
};

type FeeFormFields = BaseFeesFieldType & {
  customerAcquiringFee: number | string;
  customerAgentFee: number | string;
  customerAdditionalRequestsFee: number | string;
  customerAmountDiscount: number | string;
  customerDiscountThreshold: number | string;
  lpAmountDiscount: number | string;
  lpDiscountThreshold: number | string;
  lpAcquiringFee: number | string;
};

const toPercent = (value: number | null | undefined) =>
  value !== null && value !== undefined ? formatWithDecimals(value * 100) : "";

const EDIT_FEE_MODAL_TITLE = "Редактирование комиссии сделки";
const CONFIRM_FEE_MODAL_TITLE = "Подтвердите действие";

export const UpdateFeeModal = observer(({ config }: UpdateFeeModalProps) => {
  const { actionModals, settings } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);
  const [isConfirmStep, setIsConfirmStep] = useState(false);
  const [hasInvalidFields, setHasInvalidFields] = useState(false);

  const [form] = Form.useForm<FeeFormFields>();

  const customerBaseFeeExp = Form.useWatch("customerBaseFeeExp", form);
  const customerBaseFeeImp = Form.useWatch("customerBaseFeeImp", form);
  const lpBaseFeeExp = Form.useWatch("lpBaseFeeExp", form);
  const lpBaseFeeImp = Form.useWatch("lpBaseFeeImp", form);

  const customerAcquiringFee = Form.useWatch("customerAcquiringFee", form);
  const customerAgentFee = Form.useWatch("customerAgentFee", form);
  const customerAdditionalRequestsFee = Form.useWatch("customerAdditionalRequestsFee", form);
  const customerAmountDiscount = Form.useWatch("customerAmountDiscount", form);
  const customerDiscountThreshold = Form.useWatch("customerDiscountThreshold", form);
  const lpAcquiringFee = Form.useWatch("lpAcquiringFee", form);
  const lpAmountDiscount = Form.useWatch("lpAmountDiscount", form);
  const lpDiscountThreshold = Form.useWatch("lpDiscountThreshold", form);

  useEffect(() => {
    if (!ADDITIONAL_FEES || !config) {
      return;
    }

    config.options.title = isConfirmStep ? CONFIRM_FEE_MODAL_TITLE : EDIT_FEE_MODAL_TITLE;
  }, [config, isConfirmStep]);

  const isEmpty = (value: number | string | undefined) =>
    value === undefined || value === null || String(value).length === 0;

  const baseFeeValues = [customerBaseFeeExp, customerBaseFeeImp, lpBaseFeeExp, lpBaseFeeImp];
  const baseFieldsEmpty = baseFeeValues.some(isEmpty);
  const baseFieldsOutOfRange = baseFeeValues.some((value) => {
    const numericValue = toNumberOrZero(value);
    return Number.isNaN(numericValue) || numericValue < 0 || numericValue >= 100;
  });

  const additionalFieldsEmpty =
    isEmpty(customerAcquiringFee) ||
    isEmpty(customerAgentFee) ||
    isEmpty(customerAdditionalRequestsFee) ||
    isEmpty(customerAmountDiscount) ||
    isEmpty(customerDiscountThreshold) ||
    isEmpty(lpAcquiringFee) ||
    isEmpty(lpAmountDiscount) ||
    isEmpty(lpDiscountThreshold);

  const customerFeesSum =
    toNumberOrZero(customerAcquiringFee) +
    toNumberOrZero(customerAgentFee) +
    toNumberOrZero(customerAdditionalRequestsFee);

  const customerDiscountValue = toNumberOrZero(customerAmountDiscount);
  const lpDiscountValue = toNumberOrZero(lpAmountDiscount);

  const customerDiscountLimit = Math.min(
    customerFeesSum + toNumberOrZero(customerBaseFeeImp),
    customerFeesSum + toNumberOrZero(customerBaseFeeExp)
  );
  const lpDiscountLimit =
    Math.min(toNumberOrZero(lpBaseFeeImp), toNumberOrZero(lpBaseFeeExp)) + toNumberOrZero(lpAcquiringFee);

  const discountExceedsFee = customerDiscountValue > customerDiscountLimit || lpDiscountValue > lpDiscountLimit;

  const computeCustomerDiscountLimit = (getFieldValue: (name: string | string[]) => any) => {
    const sum =
      toNumberOrZero(getFieldValue("customerAcquiringFee")) +
      toNumberOrZero(getFieldValue("customerAgentFee")) +
      toNumberOrZero(getFieldValue("customerAdditionalRequestsFee"));
    return Math.min(
      sum + toNumberOrZero(getFieldValue("customerBaseFeeImp")),
      sum + toNumberOrZero(getFieldValue("customerBaseFeeExp"))
    );
  };

  const computeLpDiscountLimit = (getFieldValue: (name: string | string[]) => any) =>
    Math.min(toNumberOrZero(getFieldValue("lpBaseFeeImp")), toNumberOrZero(getFieldValue("lpBaseFeeExp"))) +
    toNumberOrZero(getFieldValue("lpAcquiringFee"));

  const DISCOUNT_EXCEEDS_FEE_MESSAGE = "Скидка за сумму не может превышать ставку комиссий";
  const baseFeeMaxRule = _lessThan(100, "Комиссия должна быть меньше 100%");

  const disabled = ADDITIONAL_FEES
    ? baseFieldsEmpty || baseFieldsOutOfRange || additionalFieldsEmpty || discountExceedsFee || hasInvalidFields
    : baseFieldsEmpty || baseFieldsOutOfRange || hasInvalidFields;

  const customerFees = settings.requestFeeSettings.find((item) => item.role === COMPANY_ROLE_ENUM.CUSTOMER);
  const lpFees = settings.requestFeeSettings.find((item) => item.role === COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER);

  const buildBaseFeesPayload = (values: FeeFormFields) => ({
    customerBaseFeeExp: toFractionOrZero(values.customerBaseFeeExp),
    customerBaseFeeImp: toFractionOrZero(values.customerBaseFeeImp),
    lpBaseFeeExp: toFractionOrZero(values.lpBaseFeeExp),
    lpBaseFeeImp: toFractionOrZero(values.lpBaseFeeImp),
  });

  const buildRequestFeeSettingsPayload = (values: FeeFormFields): RequestFeeSettingsPayload => ({
    feeSettings: [
      {
        role: COMPANY_ROLE_ENUM.CUSTOMER,
        direction: "IMP",
        acquiringFee: toFractionOrZero(values.customerAcquiringFee),
        agentFee: toFractionOrZero(values.customerAgentFee),
        additionalRequestsFee: toFractionOrZero(values.customerAdditionalRequestsFee),
        amountDiscount: toFractionOrZero(values.customerAmountDiscount),
        discountThreshold: toNumberOrZero(values.customerDiscountThreshold),
      },
      {
        role: COMPANY_ROLE_ENUM.CUSTOMER,
        direction: "EXP",
        acquiringFee: toFractionOrZero(values.customerAcquiringFee),
        agentFee: toFractionOrZero(values.customerAgentFee),
        additionalRequestsFee: toFractionOrZero(values.customerAdditionalRequestsFee),
        amountDiscount: toFractionOrZero(values.customerAmountDiscount),
        discountThreshold: toNumberOrZero(values.customerDiscountThreshold),
      },
      {
        role: COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER,
        direction: "IMP",
        acquiringFee: toFractionOrZero(values.lpAcquiringFee),
        amountDiscount: toFractionOrZero(values.lpAmountDiscount),
        discountThreshold: toNumberOrZero(values.lpDiscountThreshold),
      },
      {
        role: COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER,
        direction: "EXP",
        acquiringFee: toFractionOrZero(values.lpAcquiringFee),
        amountDiscount: toFractionOrZero(values.lpAmountDiscount),
        discountThreshold: toNumberOrZero(values.lpDiscountThreshold),
      },
    ],
  });

  const saveAllFees = async () => {
    const values = form.getFieldsValue(true) as FeeFormFields;
    const baseFeesPayload = buildBaseFeesPayload(values);
    const requestFeeSettingsPayload = buildRequestFeeSettingsPayload(values);

    const res = await requestWithNotify({
      fetchFn: () => updateCompanyFees(baseFeesPayload),
      openNotification,
      showErrorNotification: true,
    });

    if (!res.result) {
      return false;
    }

    const requestFeeSettingsRes = await requestWithNotify({
      fetchFn: () => updateRequestFeeSettings(requestFeeSettingsPayload),
      openNotification,
      showErrorNotification: true,
      delay: 500,
    });

    return requestFeeSettingsRes.result;
  };

  const approvedFields = async () => {
    try {
      await form.validateFields();
      setIsConfirmStep(true);
    } catch {
      // Ant Design highlights invalid fields after validation fails.
    }
  };

  const confirmAllFees = async () => {
    setLoading(true);

    try {
      const isSaved = await saveAllFees();

      if (isSaved) {
        openNotification({
          message: "Настройки комиссий обновлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setTimeout(() => {
          settings.fetchCompanyFees();
          settings.fetchRequestFeeSettings();
        }, 1500);
        handleCancelEditFee();
      }
    } finally {
      setLoading(false);
    }
  };

  const editFeeHandler = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    const baseFeesPayload = buildBaseFeesPayload(values);

    try {
      await updateCompanyFees(baseFeesPayload);
      openNotification({
        message: "Данные успешно обновлены",
        type: NOTIFICATION_TYPE_ENUM.SUCCESS,
      });
      setTimeout(() => settings.fetchCompanyFees(), 1500);
      handleCancelEditFee();
    } catch (e) {
      openNotification({
        description: parseErrorMessage(e as NormalizedError),
        type: NOTIFICATION_TYPE_ENUM.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEditFee = () => {
    actionModals.closeLastModal();
  };

  return (
    <Flex vertical>
      <Form
        form={form}
        name="editFee"
        initialValues={{
          customerBaseFeeExp: toPercent(settings.companyFees.customerBaseFeeExp),
          customerBaseFeeImp: toPercent(settings.companyFees.customerBaseFeeImp),
          lpBaseFeeExp: toPercent(settings.companyFees.lpBaseFeeExp),
          lpBaseFeeImp: toPercent(settings.companyFees.lpBaseFeeImp),
          customerAcquiringFee: toPercent(customerFees?.acquiringFee),
          lpAcquiringFee: toPercent(lpFees?.acquiringFee),
          customerAgentFee: toPercent(customerFees?.agentFee),
          customerAdditionalRequestsFee: toPercent(customerFees?.additionalRequestsFee),
          customerAmountDiscount: toPercent(customerFees?.amountDiscount),
          customerDiscountThreshold:
            customerFees?.discountThreshold !== undefined ? formatWithDecimals(customerFees.discountThreshold, 2) : "",
          lpAmountDiscount: toPercent(lpFees?.amountDiscount),
          lpDiscountThreshold:
            lpFees?.discountThreshold !== undefined ? formatWithDecimals(lpFees.discountThreshold, 2) : "",
        }}
        onFinish={() => {
          if (!ADDITIONAL_FEES) {
            editFeeHandler();
          }
        }}
        onFieldsChange={(_, allFields) => {
          setHasInvalidFields(allFields.some((field) => field.validating || !!field.errors?.length));
        }}
      >
        {ADDITIONAL_FEES && isConfirmStep ? (
          <Flex vertical gap="middle">
            <Typography.Text>Вы уверены, что хотите изменить настройки комиссий и скидок?</Typography.Text>
            <Flex gap="large">
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="primary"
                onClick={confirmAllFees}
                className="full-width"
              >
                Подтвердить
              </CustomButton>
              <CustomButton
                htmlType="button"
                disabled={loading}
                loading={loading}
                type="default"
                onClick={() => setIsConfirmStep(false)}
                className="full-width"
              >
                Отмена
              </CustomButton>
            </Flex>
          </Flex>
        ) : (
          <>
            <Flex vertical gap="middle">
              <Flex vertical>
                <Typography.Text className="margin-bottom-xs" strong>
                  Комиссии УВЭД
                </Typography.Text>
                <Flex vertical>
                  <Form.Item
                    name="customerBaseFeeExp"
                    rules={[
                      _isRequired("Поле обязательно для ввода"),
                      _greaterOrEqual(0),
                      _maxDecimals(2),
                      baseFeeMaxRule,
                    ]}
                    validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  >
                    <NumberInput
                      placeholder={
                        ADDITIONAL_FEES ? "Основная ставка (экспорт), %" : `Комиссия за экспортную сделку (УВЭД), %`
                      }
                      disabled={loading}
                    />
                  </Form.Item>
                </Flex>
                <Flex vertical>
                  <Form.Item
                    name="customerBaseFeeImp"
                    rules={[
                      _isRequired("Поле обязательно для ввода"),
                      _greaterOrEqual(0),
                      _maxDecimals(2),
                      baseFeeMaxRule,
                    ]}
                    validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  >
                    <NumberInput
                      placeholder={
                        ADDITIONAL_FEES ? "Основная ставка (импорт), %" : `Комиссия за импортную сделку (УВЭД), %`
                      }
                      disabled={loading}
                    />
                  </Form.Item>
                </Flex>
                {ADDITIONAL_FEES && (
                  <>
                    <Flex vertical>
                      <Form.Item
                        name="customerAcquiringFee"
                        rules={[
                          _isRequired("Поле обязательно для ввода"),
                          _greaterOrEqual(0),
                          _maxDecimals(2),
                          _lessOrEqual(100),
                        ]}
                        validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      >
                        <NumberInput placeholder={"Комиссия за эквайринг (импорт), %"} disabled={loading} />
                      </Form.Item>
                    </Flex>
                    <Flex vertical>
                      <Form.Item
                        name="customerAgentFee"
                        rules={[
                          _isRequired("Поле обязательно для ввода"),
                          _greaterOrEqual(0),
                          _maxDecimals(2),
                          _lessOrEqual(100),
                        ]}
                        validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      >
                        <NumberInput placeholder={"За платёжного агента, %"} disabled={loading} />
                      </Form.Item>
                    </Flex>
                    <Flex vertical>
                      <Form.Item
                        name="customerAdditionalRequestsFee"
                        rules={[
                          _isRequired("Поле обязательно для ввода"),
                          _greaterOrEqual(0),
                          _maxDecimals(2),
                          _lessOrEqual(100),
                        ]}
                        validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      >
                        <NumberInput placeholder={"За доп. заявки, %"} disabled={loading} />
                      </Form.Item>
                    </Flex>
                    <Flex gap="12px" className="full-width">
                      <Flex vertical className="full-width">
                        <Form.Item
                          name="customerAmountDiscount"
                          dependencies={[
                            "customerAcquiringFee",
                            "customerAgentFee",
                            "customerAdditionalRequestsFee",
                            "customerBaseFeeImp",
                            "customerBaseFeeExp",
                          ]}
                          rules={[
                            _isRequired("Поле обязательно для ввода"),
                            _greaterOrEqual(0),
                            _maxDecimals(2),
                            _lessOrEqual(100),
                            _lessOrEqualToComputed(computeCustomerDiscountLimit, DISCOUNT_EXCEEDS_FEE_MESSAGE),
                          ]}
                          validateDebounce={FORM_VALIDATION_DEBOUNCE}
                        >
                          <NumberInput placeholder={"Скидка за сумму, %"} disabled={loading} />
                        </Form.Item>
                      </Flex>
                      <Flex vertical className="full-width">
                        <Form.Item
                          name="customerDiscountThreshold"
                          rules={[_isRequired("Поле обязательно для ввода"), _greaterOrEqual(0)]}
                          validateDebounce={FORM_VALIDATION_DEBOUNCE}
                        >
                          <NumberInput placeholder={"Порог для скидки, руб."} disabled={loading} />
                        </Form.Item>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>

              <Flex vertical>
                <Typography.Text className="margin-bottom-xs" strong>
                  Комиссии ПЛ
                </Typography.Text>

                <Flex vertical>
                  <Form.Item
                    name="lpBaseFeeExp"
                    rules={[
                      _isRequired("Поле обязательно для ввода"),
                      _greaterOrEqual(0),
                      _maxDecimals(2),
                      baseFeeMaxRule,
                    ]}
                    validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  >
                    <NumberInput
                      placeholder={
                        ADDITIONAL_FEES ? "Основная ставка (экспорт), %" : `Комиссия за экспортную сделку (ПЛ), %`
                      }
                      disabled={loading}
                    />
                  </Form.Item>
                </Flex>
                <Flex vertical>
                  <Form.Item
                    name="lpBaseFeeImp"
                    rules={[
                      _isRequired("Поле обязательно для ввода"),
                      _greaterOrEqual(0),
                      _maxDecimals(2),
                      baseFeeMaxRule,
                    ]}
                    validateDebounce={FORM_VALIDATION_DEBOUNCE}
                  >
                    <NumberInput
                      placeholder={
                        ADDITIONAL_FEES ? "Основная ставка (импорт), %" : `Комиссия за импортную сделку (ПЛ), %`
                      }
                      disabled={loading}
                    />
                  </Form.Item>
                </Flex>
                {ADDITIONAL_FEES && (
                  <>
                    <Flex vertical>
                      <Form.Item
                        name="lpAcquiringFee"
                        rules={[
                          _isRequired("Поле обязательно для ввода"),
                          _greaterOrEqual(0),
                          _maxDecimals(2),
                          _lessOrEqual(100),
                        ]}
                        validateDebounce={FORM_VALIDATION_DEBOUNCE}
                      >
                        <NumberInput placeholder={"Комиссия за эквайринг (экспорт), %"} disabled={loading} />
                      </Form.Item>
                    </Flex>
                    <Flex gap="12px" className="full-width">
                      <Flex vertical className="full-width">
                        <Form.Item
                          name="lpAmountDiscount"
                          dependencies={["lpBaseFeeImp", "lpBaseFeeExp", "lpAcquiringFee"]}
                          rules={[
                            _isRequired("Поле обязательно для ввода"),
                            _greaterOrEqual(0),
                            _maxDecimals(2),
                            _lessOrEqual(100),
                            _lessOrEqualToComputed(computeLpDiscountLimit, DISCOUNT_EXCEEDS_FEE_MESSAGE),
                          ]}
                          validateDebounce={FORM_VALIDATION_DEBOUNCE}
                        >
                          <NumberInput placeholder={"Скидка за сумму, %"} disabled={loading} />
                        </Form.Item>
                      </Flex>
                      <Flex vertical className="full-width">
                        <Form.Item
                          name="lpDiscountThreshold"
                          rules={[_isRequired("Поле обязательно для ввода"), _greaterOrEqual(0)]}
                          validateDebounce={FORM_VALIDATION_DEBOUNCE}
                        >
                          <NumberInput placeholder={"Порог для скидки, руб."} disabled={loading} />
                        </Form.Item>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>
            </Flex>

            <Form.Item>
              <Flex gap="large">
                {!ADDITIONAL_FEES ? (
                  <CustomButton
                    htmlType="submit"
                    disabled={loading || disabled}
                    loading={loading}
                    className="full-width"
                  >
                    Сохранить изменения
                  </CustomButton>
                ) : (
                  <CustomButton
                    disabled={loading || disabled}
                    loading={loading}
                    type="primary"
                    onClick={approvedFields}
                    className="full-width"
                  >
                    Сохранить
                  </CustomButton>
                )}
                <CustomButton
                  htmlType="button"
                  disabled={loading}
                  loading={loading}
                  type="default"
                  onClick={handleCancelEditFee}
                  className="full-width"
                >
                  Отменить
                </CustomButton>
              </Flex>
            </Form.Item>
          </>
        )}
      </Form>
    </Flex>
  );
});
